import { ArrowLeft, CheckCircle, Save, Store } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../../../assets/scss/pages/SettingsPage.module.scss';
import { getShop, updateShop } from '../../../api/shop.api';
import { useAuthStore } from '../../../stores/authStore';

const FIELDS = [
    { key: 'nameShop', label: 'Tên cửa hàng', placeholder: 'Nhập tên cửa hàng...', type: 'text' },
    { key: 'nameAdmin', label: 'Tên chủ cửa hàng', placeholder: 'Nhập tên chủ cửa hàng...', type: 'text' },
    { key: 'phoneOne', label: 'Số điện thoại 1', placeholder: 'Nhập số điện thoại...', type: 'tel' },
    { key: 'phoneTwo', label: 'Số điện thoại 2', placeholder: 'Nhập số điện thoại...', type: 'tel' },
    { key: 'address', label: 'Địa chỉ', placeholder: 'Nhập địa chỉ...', type: 'textarea' },
];

const EDITABLE_KEYS = FIELDS.map((f) => f.key);

export default function ShopSettings() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const shopId = user?.shopId;

    const [shopInfo, setShopInfo] = useState(null);
    const [form, setForm] = useState({ nameShop: '', nameAdmin: '', phoneOne: '', phoneTwo: '', address: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [toast, setToast] = useState(null);

    const showToast = useCallback((type, text) => {
        setToast({ type, text });
        setTimeout(() => setToast(null), 2500);
    }, []);

    const loadShop = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            const { data } = await getShop(shopId);
            setShopInfo(data);
            setForm({
                nameShop: data.nameShop || '',
                nameAdmin: data.nameAdmin || '',
                phoneOne: data.phoneOne || '',
                phoneTwo: data.phoneTwo || '',
                address: data.address || '',
            });
        } catch {
            setError('Không thể tải thông tin cửa hàng');
        } finally {
            setLoading(false);
        }
    }, [shopId]);

    useEffect(() => { loadShop(); }, [loadShop]);

    const handleEdit = () => {
        setForm({
            nameShop: shopInfo.nameShop || '',
            nameAdmin: shopInfo.nameAdmin || '',
            phoneOne: shopInfo.phoneOne || '',
            phoneTwo: shopInfo.phoneTwo || '',
            address: shopInfo.address || '',
        });
        setError('');
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setError('');
        setForm({
            nameShop: shopInfo.nameShop || '',
            nameAdmin: shopInfo.nameAdmin || '',
            phoneOne: shopInfo.phoneOne || '',
            phoneTwo: shopInfo.phoneTwo || '',
            address: shopInfo.address || '',
        });
    };

    const handleSave = async () => {
        if (submitting) return;
        const changedFields = {};
        EDITABLE_KEYS.forEach((key) => {
            if (form[key] !== (shopInfo[key] || '')) {
                changedFields[key] = form[key];
            }
        });
        if (Object.keys(changedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        setSubmitting(true);
        setError('');
        try {
            await updateShop(shopId, changedFields);
            setShopInfo((prev) => ({ ...prev, ...changedFields }));
            setIsEditing(false);
            showToast('success', 'Cập nhật thông tin cửa hàng thành công');
        } catch (err) {
            const msg = err.response?.data?.message;
            setError(Array.isArray(msg) ? msg.join(', ') : msg || 'Lưu thất bại');
        } finally {
            setSubmitting(false);
        }
    };

    const onChange = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setError('');
    };

    return (
        <div className={styles.page}>
            {/* Header */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ArrowLeft size={18} />
                </button>
                <div>
                    <h1 className={styles.title}>Thông tin cửa hàng</h1>
                    <div className={styles.subtitle}>Xem và cập nhật thông tin cơ bản của cửa hàng</div>
                </div>
            </div>

            {loading ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#6B7280' }}>Đang tải...</div>
            ) : (
                <div className={styles.printerLayout}>
                    {/* LEFT: form fields */}
                    <div className={styles.printerLeft}>
                        {/* Editable fields */}
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionTitle}>Thông tin cơ bản</div>
                            </div>
                            <div className={styles.form}>
                                {FIELDS.map(({ key, label, placeholder, type }) => (
                                    <div key={key} className={styles.formGroup}>
                                        <div className={styles.formLabel}>{label}</div>
                                        {type === 'textarea' ? (
                                            <textarea
                                                className={styles.formInput}
                                                style={{ height: 90, padding: '10px 14px', resize: 'vertical' }}
                                                value={form[key]}
                                                onChange={(e) => onChange(key, e.target.value)}
                                                placeholder={placeholder}
                                                disabled={!isEditing}
                                            />
                                        ) : (
                                            <input
                                                className={styles.formInput}
                                                type={type}
                                                value={form[key]}
                                                onChange={(e) => onChange(key, e.target.value)}
                                                placeholder={placeholder}
                                                disabled={!isEditing}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Read-only info */}
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <div className={styles.sectionTitle}>Thông tin hệ thống</div>
                            </div>
                            <div className={styles.form}>
                                <div className={styles.formGroup}>
                                    <div className={styles.formLabel}>Mã cửa hàng</div>
                                    <input
                                        className={styles.formInput}
                                        value={shopInfo?.code || ''}
                                        disabled
                                        readOnly
                                    />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A' }}>Trạng thái</span>
                                    <span
                                        className={shopInfo?.isActive ? styles.statusActive : styles.statusInactive}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 5,
                                            padding: '3px 10px',
                                            borderRadius: 999,
                                            fontSize: 12,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {shopInfo?.isActive ? 'Hoạt động' : 'Không hoạt động'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div style={{ color: '#EF4444', fontSize: 13, fontWeight: 600, padding: '8px 12px', background: '#FFF5F5', borderRadius: 8, border: '1px solid #FECACA' }}>
                                {error}
                            </div>
                        )}
                    </div>

                    {/* RIGHT: action panel */}
                    <div className={styles.printerRight}>
                        <div className={styles.printerActionCard}>
                            <div className={styles.printerStatus}>
                                <Store size={20} color="#0089F6" />
                                <div>
                                    <div className={styles.printerStatusLabel}>
                                        <span style={{ fontWeight: 700 }}>{shopInfo?.nameShop || '—'}</span>
                                    </div>
                                    <div className={styles.printerStatusMeta}>{shopInfo?.code}</div>
                                </div>
                            </div>

                            <div className={styles.printerDivider} />

                            {isEditing ? (
                                <>
                                    <button
                                        className={styles.btnSave}
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={handleSave}
                                        disabled={submitting}
                                    >
                                        <Save size={15} />
                                        {submitting ? 'Đang lưu...' : 'Lưu thay đổi'}
                                    </button>
                                    <button
                                        className={styles.btnTest}
                                        onClick={handleCancel}
                                        disabled={submitting}
                                    >
                                        Hủy
                                    </button>
                                </>
                            ) : (
                                <button
                                    className={styles.btnSave}
                                    style={{ width: '100%', justifyContent: 'center' }}
                                    onClick={handleEdit}
                                >
                                    Chỉnh sửa
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && (
                <div className={`${styles.toast} ${toast.type === 'success' ? styles.toastSuccess : styles.toastError}`}>
                    {toast.type === 'success' ? <CheckCircle size={15} /> : null}
                    {toast.text}
                </div>
            )}
        </div>
    );
}
